import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  IconButton,
  Slide,
  Typography,
  TextField,
  Fade,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import Rating from '@mui/material/Rating';
import API from '../api/api.js';
import { useSelector, useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

const customIcons = {
  1: {
    icon: <SentimentVeryDissatisfiedIcon fontSize="large" />, label: 'Very Dissatisfied',
  },
  2: {
    icon: <SentimentDissatisfiedIcon fontSize="large" />, label: 'Dissatisfied',
  },
  3: {
    icon: <SentimentSatisfiedIcon fontSize="large" />, label: 'Neutral',
  },
  4: {
    icon: <SentimentSatisfiedAltIcon fontSize="large" />, label: 'Satisfied',
  },
  5: {
    icon: <SentimentVerySatisfiedIcon fontSize="large" />, label: 'Very Satisfied',
  },
};

const styles = {
  root: {
    width: 450,
    maxWidth: 450,
    position: 'fixed',
    bottom: '65px',
    right: '24px',
    overflow: 'hidden',
    zIndex: 100,
    textAlign: 'center',
  },
  ratingContainer: {
    textAlign: 'center',
    margin: '.5rem 0',
    '& svg': {
      width: '2.5rem',
      height: '2.5rem',
      margin: '4px',
    },
  },
  closeBtn: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    cursor: 'pointer',
  },
  backBtn: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    cursor: 'pointer',
    color: 'lightgray',
  },
  cardContent: {
    width: '100%',
  },
  cardActions: {
    justifyContent: 'end',
  },
  hiddenContainer: {
    position: 'relative',
    width: '100%',
    top: 0,
    left: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  feedbackForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '1rem',
    width: '100%',
  },
  innerCardContent: {
    padding: '16px',
    marginTop: '24px',
  },
  checkIcon: {
    fontSize: 80,
    color: 'green',
  },
};

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{customIcons[value].icon}</span>;
}

IconContainer.propTypes = {
  value: PropTypes.number.isRequired,
};

export default function SatisfactionQuote() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [feedback, setFeedback] = useState('');

  const currentPlan = useSelector((state) => state.shop.shop.charge);

  useEffect(() => {
    const isClosed = window.localStorage.getItem('ly-satisfaction-quote-dismissed');
    if (!isClosed) {
      setTimeout(() => {
        if (isBetween7And30Days(currentPlan.activated_on)) {
          setOpen(true);
        }
      }, 3000);
    }
  }, []);

  const handleClose = () => {
    window.localStorage.setItem('ly-satisfaction-quote-dismissed', true);
    setOpen(false);
  };

  const handleRatingChange = (event, value) => {
    setRate(value);
    if (value >= 4) {
      setTimeout(() => setShowThankYou(true), 300);
    } else {
      setTimeout(() => setShowForm(true), 300);
    }
  };

  const handleBack = () => {
    setShowThankYou(false);
    setShowForm(false);
    setRate(null);
  };

  const writeReview = () => {
    handleClose();
    window.open('https://apps.shopify.com/langify/reviews', '_blank');
  }

  const handleSubmit = () => {
    setSendingFeedback(true);
    let feedbackString = `
    ${feedback}

    ---------------
    
    ${rate} Star${rate > 1 && 's'} quoted.
    `;
    API.sendAppFeedback(feedbackString, () => {
      setSendingFeedback(false);
      setShowForm(false);
      setShowCheck(true);
      setTimeout(() => {
        handleClose();
      }, 5000);
    });
  };

  function isBetween7And30Days(dateString) {
    const inputDate = new Date(dateString);
    const today = new Date();
    const diffTime = today - inputDate;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    //console.log(diffDays)
    return diffDays >= 7 && diffDays <= 30;
  }

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Card sx={styles.root} elevation={10}>
        <Fade in={showCheck} unmountOnExit>
          <Box sx={{...styles.hiddenContainer, padding: '24px'}}>
            <Typography variant="h4" align="center" gutterBottom>
              {t('satisfactionQuote.thankYou')}
            </Typography>
            <CheckCircleIcon sx={styles.checkIcon} />
          </Box>
        </Fade>

        <Fade in={!rate} unmountOnExit>
          <CardActionArea>
            <CardContent sx={styles.cardContent}>
              <Typography gutterBottom variant="h5" component="h2">
                {t('satisfactionQuote.title')}
              </Typography>
              <IconButton onClick={handleClose} color="inherit" size="medium" sx={styles.closeBtn}>
                <CloseIcon />
              </IconButton>

              <Box sx={styles.ratingContainer}>
                <Rating
                  onChange={handleRatingChange}
                  name="sentiment-rating"
                  IconContainerComponent={IconContainer}
                  highlightSelectedOnly
                  max={5}
                  size="large"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" component="p">
                {t('satisfactionQuote.subline')}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Fade>

        <Fade in={showThankYou} unmountOnExit>
          <Box sx={styles.hiddenContainer}>
            <CardContent sx={styles.cardContent}>
              <Button
                onClick={handleBack}
                variant="text"
                size="medium"
                sx={styles.backBtn}>
                <ArrowBackIcon /> {t('satisfactionQuote.back')}
              </Button>
              <IconButton onClick={handleClose} color="inherit" size="medium" sx={styles.closeBtn}>
                <CloseIcon />
              </IconButton>
              <Box sx={styles.innerCardContent}>
                <Typography variant="h4" align="center" gutterBottom>
                  {t(`satisfactionQuote.thanksTitle${rate}`)}
                </Typography>
                <Typography variant="body1">
                  {t(`satisfactionQuote.thanksDescription${rate}`)}
                </Typography>
              </Box>
              <CardActions sx={styles.cardActions}>
                <Button
                  size="large"
                  color="primary"
                  variant="contained"
                  onClick={writeReview}
                  fullWidth
                >
                  {t('satisfactionQuote.reviewBtn')}
                </Button>
              </CardActions>
            </CardContent>
          </Box>
        </Fade>

        <Fade in={showForm} unmountOnExit>
          <Box sx={styles.hiddenContainer}>
            <CardContent sx={styles.cardContent}>
              <Button
                onClick={handleBack}
                variant="text"
                size="medium"
                sx={styles.backBtn}>
                <ArrowBackIcon /> {t('satisfactionQuote.back')}
              </Button>
              <IconButton onClick={handleClose} color="inherit" size="medium" sx={styles.closeBtn}>
                <CloseIcon />
              </IconButton>
              <Box sx={styles.innerCardContent}>
                <Typography variant="h4" align="center" gutterBottom>
                  {t(`satisfactionQuote.thanksTitle${rate}`)}
                </Typography>
                <Typography variant="body1">
                  {t(`satisfactionQuote.thanksDescription${rate}`)}
                </Typography>
                <Box sx={styles.feedbackForm}>
                  <TextField
                    label={t('satisfactionQuote.feedbackLabel')}
                    multiline
                    rows={3}
                    variant="outlined"
                    fullWidth
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    disabled={sendingFeedback}
                  />
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!feedback || sendingFeedback}
                    fullWidth
                  >
                    {sendingFeedback ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('satisfactionQuote.submitBtn')
                    )}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Box>
        </Fade>
      </Card>
    </Slide>
  );
}